"use client";

import * as React from "react";

import {
  PopoverMenu,
  PopoverMenuItem,
  PopoverMenuSearchSection,
  PopoverMenuScrollSection,
  PopoverMenuSection,
} from "@/components/ui/popover-menu";

import { cn } from "@/lib/utils";


export type AccountOption = {
  id: string;
  name: string;
  domain: string | null;
  logoUrl: string | null;
};

type Props = {
  value: string | null;
  onChange: (accountId: string | null) => void;
  accounts: AccountOption[];
  trigger: React.ReactElement;
  disabled?: boolean;
};

function accountInitial(a: AccountOption): string {
  return (a.name?.trim() || a.domain || "?").slice(0, 1).toUpperCase();
}

export function AccountAvatar({
  account,
  className,
}: {
  account: AccountOption;
  className?: string;
}) {
  if (account.logoUrl) {
    return (
      // eslint-disable-next-line @next/next/no-img-element
      <img
        src={account.logoUrl}
        alt=""
        className={cn("size-4 shrink-0 rounded-md object-cover", className)}
      />
    );
  }
  return (
    <span
      className={cn(
        "flex size-4 shrink-0 items-center justify-center rounded-md bg-neutral-04 text-[9px] font-medium text-neutral-11",
        className,
      )}
    >
      {accountInitial(account)}
    </span>
  );
}

export function TaskAccountMenu({
  value,
  onChange,
  accounts,
  trigger,
  disabled,
}: Props) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState("");

  const current = React.useMemo(
    () => accounts.find((a) => a.id === value) ?? null,
    [accounts, value],
  );

  const others = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q
      ? accounts.filter((a) => {
          const name = a.name.toLowerCase();
          const domain = (a.domain ?? "").toLowerCase();
          return name.includes(q) || domain.includes(q);
        })
      : accounts;
    return filtered.filter((a) => a.id !== value);
  }, [accounts, query, value]);

  const select = (accountId: string | null) => {
    onChange(accountId);
    setOpen(false);
    setQuery("");
  };

  return (
    <PopoverMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
      trigger={trigger}
      menuContentClassName="w-[15.125rem] p-0"
      menu={
        <>
          <PopoverMenuSearchSection
            fullWidthDividerAfter
            inputProps={{
              autoFocus: true,
              value: query,
              onChange: (event) => setQuery(event.target.value),
              placeholder: "Search...",
            }}
          />

          {current ? (
            <PopoverMenuSection fullWidthDividerAfter className="px-1">
              <PopoverMenuItem
                icon={<AccountAvatar account={current} />}
                label={current.name}
                selected
                disabled={disabled}
                onClick={() => select(null)}
              />
            </PopoverMenuSection>
          ) : null}

          <PopoverMenuScrollSection
            className="px-1 pb-1.5"
            maxHeightClassName="max-h-[12rem]"
          >
            {others.length === 0 ? (
              <p className="px-2 py-1.5 text-[12px] text-neutral-07">
                No matches.
              </p>
            ) : (
              others.map((a) => (
                <PopoverMenuItem
                  key={a.id}
                  icon={<AccountAvatar account={a} />}
                  label={a.name}
                  disabled={disabled}
                  onClick={() => select(a.id)}
                />
              ))
            )}
          </PopoverMenuScrollSection>
        </>
      }
    />
  );
}
